import type { SemanticModel } from '@aibi/contracts';
import { SemanticModelIndex } from '@aibi/semantic-model';
import { useCallback, useMemo, useReducer, type ReactNode } from 'react';
import type { CommandContext } from '../model/analysis-commands';
import { buildFieldCatalog } from '../model/field-catalog';
import { canRedo, canUndo } from '../model/history';
import type { WorkspaceDocument } from '../model/workspace-document';
import {
  createWorkspaceStoreState,
  workspaceStoreReducer,
  type WorkspaceAction,
  type WorkspaceStoreState,
} from '../model/workspace-store';
import {
  WorkspaceActionsContext,
  WorkspaceViewContext,
  type WorkspaceActions,
  type WorkspaceAudience,
  type WorkspaceView,
} from './workspace-context';

export interface WorkspaceProviderProps {
  readonly model: SemanticModel;
  readonly initialDocument: WorkspaceDocument;
  readonly audience?: WorkspaceAudience;
  readonly children: ReactNode;
}

/**
 * Estado del documento (AnalysisState por tarjeta) con historial undo/redo. Se monta con
 * `key` por modelo: cambiar de modelo crea un estado nuevo. El estado de UI vive aparte.
 */
export function WorkspaceProvider({
  model,
  initialDocument,
  audience = 'editor',
  children,
}: WorkspaceProviderProps) {
  const index = useMemo(() => new SemanticModelIndex(model), [model]);
  const catalog = useMemo(() => buildFieldCatalog(index), [index]);
  const context = useMemo<CommandContext>(() => ({ index, catalog }), [index, catalog]);

  const reducer = useCallback(
    (state: WorkspaceStoreState, action: WorkspaceAction) =>
      workspaceStoreReducer(state, action, context),
    [context],
  );
  const [state, dispatch] = useReducer(reducer, initialDocument, createWorkspaceStoreState);

  const actions = useMemo<WorkspaceActions>(
    () => ({
      execute: (command) => {
        dispatch({ type: 'command', command });
      },
      undo: () => {
        dispatch({ type: 'undo' });
      },
      redo: () => {
        dispatch({ type: 'redo' });
      },
      dismissRejection: () => {
        dispatch({ type: 'dismiss-rejection' });
      },
      dispatch,
    }),
    [],
  );

  const view = useMemo<WorkspaceView>(
    () => ({
      document: state.history.present,
      model,
      index,
      catalog,
      audience,
      canUndo: canUndo(state.history),
      canRedo: canRedo(state.history),
      rejection: state.rejection,
      lastCreatedCardId: state.lastCreatedCardId,
    }),
    [state, model, index, catalog, audience],
  );

  return (
    <WorkspaceActionsContext value={actions}>
      <WorkspaceViewContext value={view}>{children}</WorkspaceViewContext>
    </WorkspaceActionsContext>
  );
}
